// Serialize one instance's live slice state into the permissive WorkspaceStateV1 autosave blob
// (APP-2705) — the counterpart of exportLayoutConfig. Unlike that strict portable projection, this
// KEEPS ai-artifact modules (pinned artifacts must restore on reopen) and folds `modelId` from the
// model slice into `core`. No client-side validation: server is the validator of record (ADR 0003)
// and the schema is permissive (allows unknown keys), so deploy skew never 400s an autosave.
import { pick } from "lodash"
import { CORE_PREFERENCE_FIELDS, type CoreRootState } from "../store/core-slice"
import { exportLayoutConfig, type DashboardPanelSlicesState } from "./export-layout-config"
import type { ModelRootState } from "../store/model-slice"
import type { TradeRootState } from "../store/trade-slice"
import { OPTIMIZATION_PERSISTED_FIELDS, type OptimizationRootState } from "../store/optimization-slice"
import type { DashboardModule } from "../modules/types"
import type { Core, ModuleEntry, WorkspaceStateV1 } from "../contracts/layouts"

// Trailing-edge autosave delay (APP-2705); exported so the sync hook and tests share one source.
export const WORKSPACE_AUTOSAVE_DEBOUNCE_MS = 2000

// serializeWorkspace needs the panel slices (shared with exportLayoutConfig) plus the core,
// model, trade, and optimization slots that live outside the portable-layout projection.
export interface WorkspaceSerializableState extends DashboardPanelSlicesState {
  dashboard: DashboardPanelSlicesState["dashboard"] & {
    core: CoreRootState
    model: ModelRootState
    trade: TradeRootState
    optimization: OptimizationRootState
  }
}

function buildCore(
  state: WorkspaceSerializableState,
  dashboardId: string,
): Core {
  const coreSlot = state.dashboard.core[dashboardId]
  // modelId lives in the separate model slice, not the core slot — merge it in so the
  // workspace carries the whole dashboard-level state in one block.
  const modelId = state.dashboard.model[dashboardId]?.modelId ?? null
  const core: Core = coreSlot ? { ...pick(coreSlot, CORE_PREFERENCE_FIELDS) } : {}
  core.modelId = modelId
  return core
}

function toModuleEntry(module: DashboardModule): ModuleEntry {
  // Keep ai-artifact and every other module verbatim — the instance restores exactly what
  // was open. config is optional in the contract; omit it when the module carries none.
  return module.config ? { id: module.id, type: module.type, config: module.config } : { id: module.id, type: module.type }
}

/**
 * Project the dashboard's live state for `dashboardId` into a WorkspaceStateV1.
 *
 * - `core`: the persisted core preference fields plus `modelId` from the model slice.
 * - `modules`: every open module INCLUDING ai-artifact (unlike exportLayoutConfig, which
 *   strips them) — the instance must restore its pinned artifacts on reopen.
 * - `panels`: per-panel portable viewConfig keyed by panel key, reusing exportLayoutConfig's
 *   slice→contract projection (so the two persistence paths can never drift).
 * - `arrangement`: the verbatim Dockview `toJSON()` blob, or omitted when none is supplied.
 *
 * Pure function — unit-tested directly against a configureStore(testReducer) state.
 */
export function serializeWorkspace(
  state: WorkspaceSerializableState,
  dashboardId: string,
  arrangement: Record<string, unknown> | null = null,
): WorkspaceStateV1 {
  // Strip transient (drilldown) modules — session-only, never persisted. But KEEP ai-artifact
  // (unlike exportLayoutConfig): pinned artifacts are instance state to restore on reopen.
  const modules = (state.dashboard.panels[dashboardId]?.modules ?? [])
    .filter((module) => module.config?.transient !== true)

  // Reuse the portable projection for per-panel viewConfigs (ai-artifact has no portable
  // viewConfig, so its absence from `panels` is correct); key by panel key.
  const { config } = exportLayoutConfig(state, dashboardId, null)
  const panels: Record<string, Record<string, unknown>> = {}
  for (const panel of config.panels) {
    panels[panel.key] = panel.viewConfig as Record<string, unknown>
  }

  const workspace: WorkspaceStateV1 = {
    schemaVersion: 1,
    core: buildCore(state, dashboardId),
    modules: modules.map(toModuleEntry),
    panels,
  }
  if (arrangement) {
    workspace.arrangement = arrangement
  }

  // trade + optimization: per-subtype writes are also suppressed in instance mode, so they must
  // ride along in the blob or be lost on reopen — carried under WorkspaceStateV1's permissive keys.
  const tradeSlot = state.dashboard.trade[dashboardId]
  if (tradeSlot && typeof tradeSlot.isExpanded === "boolean") {
    workspace.trade = { isExpanded: tradeSlot.isExpanded }
  }
  const optimizationSlot = state.dashboard.optimization[dashboardId]
  if (optimizationSlot) {
    workspace.optimization = pick(optimizationSlot, OPTIMIZATION_PERSISTED_FIELDS)
  }
  return workspace
}
